import './ViewDialog.scss';

import {
  faBoxes,
  faCheck,
  faHandshake,
  faHome,
  faIdCard,
  faMapMarkedAlt,
  faMobileAlt,
  faPhoneAlt,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Tooltip } from 'primereact/tooltip';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import useMediaQuery from '../../../../../../hooks/useMediaQuery';
import { HubType } from '../../../../../../types/api/hubs';
import { getDays } from '../CreateEditDialog/CreateEditDialog.functions';
import GeoLocation from './GeoLocation';

type Props = {
  visible: boolean;
  onHide: () => void;
  onEdit?: () => void;
  data: HubType | any;
  isLoading: boolean;
};

function ViewDialog({ visible, data, isLoading, onEdit, onHide }: Props) {
  const { t } = useTranslation();
  const days = useMemo(() => getDays(t), [t]);

  const regions = useMemo(() => {
    if (!data?.regions) {
      return;
    }

    return (
      <div>
        <span className="total-regions">
          {t('Total')}: {data.regions?.length}
        </span>
        <div className="regions-info-table">
          {data.regions?.map((region: any, i: number) => {
            return <span key={i}>- {region.name}</span>;
          })}
        </div>
      </div>
    );
  }, [data, t]);

  const dialogFooter = !isLoading && (
    <>
      {onEdit && (
        <Button label={t('Edit')} className="p-button-text" onClick={onEdit} />
      )}
      <Button label={t('Close')} onClick={() => onHide()} />
    </>
  );

  const isOnMobile = useMediaQuery('(max-width: 768px)');

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={data?.name}
      footer={dialogFooter}
      dismissableMask
      blockScroll
      resizable={false}
      maximizable={isOnMobile}
      maximized={isOnMobile}
      className="hubs-view-dialog"
    >
      <>
        <span className="section-title">{t('General information')}</span>
        <div className="quick-info-container">
          <div className="quick-info">
            <Tooltip target=".name" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                className="name"
                data-pr-tooltip={t("Warehouse's name")}
                icon={faIdCard}
              />
              <span className="name" data-pr-tooltip={t("Warehouse's name")}>
                {data?.name}
              </span>
            </div>

            <Tooltip target=".address" mouseTrack mouseTrackLeft={10} />
            <div style={{ display: 'flex' }}>
              <FontAwesomeIcon
                icon={faHome}
                className="address"
                data-pr-tooltip={t("Warehouse's address")}
              />

              <span
                className="address"
                data-pr-tooltip={t("Warehouse's address")}
              >
                {data?.address?.rawName}
              </span>
            </div>

            <Tooltip target=".mobile" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faMobileAlt}
                className="mobile"
                data-pr-tooltip={t("Warehouse's mobile")}
              />

              <span
                className="mobile"
                data-pr-tooltip={t("Warehouse's mobile")}
              >
                {data?.mobile || '-'}
              </span>
            </div>

            <Tooltip target=".phone" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faPhoneAlt}
                className="phone"
                data-pr-tooltip={t("Warehouse's phone")}
              />

              <span className="phone" data-pr-tooltip={t("Warehouse's phone")}>
                {data?.phone || '-'}
              </span>
            </div>
          </div>

          <div className="quick-info">
            <Tooltip target=".drop_point" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faMapMarkedAlt}
                className="drop_point"
                data-pr-tooltip={t(
                  'Parcels can be delivered or picked up from warehouse'
                )}
              />

              <FontAwesomeIcon
                icon={parseInt(data?.dropPoint) ? faCheck : faTimes}
                className="drop_point"
                data-pr-tooltip={t(
                  'Parcels can be delivered or picked up from warehouse'
                )}
              />
            </div>

            <Tooltip target=".sorting_center" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faBoxes}
                className="sorting_center"
                data-pr-tooltip={t('Warehouse is a sorting center')}
              />

              <FontAwesomeIcon
                icon={parseInt(data?.sortingCenter) ? faCheck : faTimes}
                className="sorting_center"
                data-pr-tooltip={t('Warehouse is a sorting center')}
              />
            </div>

            <Tooltip target=".franchise_name" mouseTrack mouseTrackLeft={10} />
            <div>
              <FontAwesomeIcon
                icon={faHandshake}
                className="franchise_name"
                data-pr-tooltip={t('Franchise type')}
              />

              <span className="franchise_name" data-pr-tooltip={t('Franchise')}>
                {data?.partnerName || '-'}
              </span>
            </div>
          </div>
        </div>

        <span className="section-title">{t('Intervals')}</span>
        <div className="time-intervals">
          {data?.reconciliationTimeInterval?.length > 0 ? (
            <div className="intervals-info-table">
              {data.reconciliationTimeInterval.map((iv: any) => (
                <div key={iv.dayOfWeek} className="time-row">
                  <label className="time-label">
                    {days.find((d) => d.dayOfWeek === iv.dayOfWeek)?.label ||
                      ''}
                  </label>
                  <div>
                    <span>
                      {iv.timeSpanFrom} - {iv.timeSpanTo}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-location-available">
              <i>{t('This warehouse has no reconciliation time defined')}</i>
            </div>
          )}
        </div>

        <span className="section-title">{t('Working hours')}</span>
        <div className="time-intervals">
          {data?.workingHours?.length > 0 ? (
            <div className="intervals-info-table">
              {data.workingHours.map((day: any) => (
                <div key={day.dayOfWeek} className="time-row">
                  <label className="time-label">
                    {days.find((d) => d.dayOfWeek === day.dayOfWeek)?.label ||
                      ''}
                  </label>
                  <div>
                    {day.openingHours && day.openingHours.length > 0 ? (
                      day.openingHours.map((interval: any, j: number) => (
                        <span
                          key={`${day.dayOfWeek}-${j}`}
                          className="time-interval"
                        >
                          {interval.from} - {interval.to}
                          {j < day.openingHours.length - 1 && (
                            <span className="separator">, </span>
                          )}
                        </span>
                      ))
                    ) : (
                      <i>{t('Closed')}</i>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-location-available">
              <i>{t('This warehouse has no working hours defined')}</i>
            </div>
          )}
        </div>

        <span className="section-title">{t('Location')}</span>
        <GeoLocation data={data} />

        <span className="section-title">{t('Regions')}</span>
        {data?.regions?.length > 1 ? (
          regions
        ) : (
          <div className="no-location-available">
            <i>{t('This warehouse has no regions defined')}</i>
          </div>
        )}
      </>
    </Dialog>
  );
}

export default ViewDialog;
